import { Blob as BlobType } from '@/services/types/blob-types';
import { CollectionNode } from '@/services/types/collection-types';

/**
 * Recursively counts all blobs in a collection and its children
 */
export const countRecursiveFiles = (
  node: CollectionNode,
  blobs: BlobType[] | undefined,
): number => {
  if (!blobs) return 0;

  // Count files directly in this collection
  const directFiles = blobs.filter(
    (blob) => blob.collection_details.id === node.id,
  ).length;

  // Recursively count files in children
  const childrenFiles =
    node.children?.reduce(
      (count, child) => count + countRecursiveFiles(child, blobs),
      0,
    ) || 0;

  return directFiles + childrenFiles;
};

/**
 * Format file size in a human-readable way
 */
export const formatFileSize = (sizeInBytes: number): string => {
  if (sizeInBytes >= 1024 * 1024) {
    return `${(sizeInBytes / (1024 * 1024)).toFixed(1)} MB`;
  }
  return `${(sizeInBytes / 1024).toFixed(1)} KB`;
};

/**
 * Format date string in different formats
 * @param dateString ISO date string
 * @param format The format to display: 'relative', 'full', or 'short'
 */
export const formatDateString = (
  dateString: string,
  format: 'relative' | 'full' | 'short' = 'short',
): string => {
  const date = new Date(dateString);
  // For invalid dates
  if (Number.isNaN(date.getTime())) {
    return 'Invalid date';
  }

  // Full format: "Monday, January 1, 2023, 12:00 PM"
  if (format === 'full') {
    return new Intl.DateTimeFormat('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      second: 'numeric',
    }).format(date);
  }

  // Short format: "Jan 1, 2023, 12:00 PM"
  if (format === 'short') {
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
    }).format(date);
  }

  // Relative format: "2 days ago", "just now", etc.
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHour = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHour / 24);
  const diffMonth = Math.floor(diffDay / 30);
  const diffYear = Math.floor(diffMonth / 12);

  if (diffSec < 60) {
    return 'just now';
  }
  if (diffMin < 60) {
    return `${diffMin} minute${diffMin === 1 ? '' : 's'} ago`;
  }
  if (diffHour < 24) {
    return `${diffHour} hour${diffHour === 1 ? '' : 's'} ago`;
  }
  if (diffDay < 30) {
    return `${diffDay} day${diffDay === 1 ? '' : 's'} ago`;
  }
  if (diffMonth < 12) {
    return `${diffMonth} month${diffMonth === 1 ? '' : 's'} ago`;
  }
  return `${diffYear} year${diffYear === 1 ? '' : 's'} ago`;
};
